import AutocompleteSearchInput from '@/components/form/AutocompleteSearchInput'
import { getFieldErrorMessages } from '@/components/form/InlineFieldErrors'
import { Button } from '@/components/ui/button'
import { ToggleButton } from '@/components/ui/toggle-button'
import { Label } from '@/components/ui/label'
import { ResizableHandle, ResizablePanel, ResizablePanelGroup } from '@/components/ui/resizable'
import { Switch } from '@/components/ui/switch'
import { Typography } from '@/components/ui/typography'
import { useFormSection } from '@/hooks/useFormSection'
import { useGoogleMaps } from '@/hooks/use-google-maps'
import { usePlacesAutocomplete } from '@/hooks/use-places-autocomplete'
import { useIsReadOnly } from '@/lib/casl'
import { DeliveryServiceabilityStatus } from '@/lib/enums/DeliveryServiceabilityStatus'
import dayjs from '@/lib/dayjs'
import { PolygonType } from '@/lib/enums/PolygonType'
import { useFormContext } from '@/lib/form-contexts'
import { usePreviewLocationDeliveryFeeMutation } from '@/lib/services/locations/locations.query'
import { cn, formatCurrency } from '@/lib/utils'
import { useStore } from '@tanstack/react-form'
import { useMediaQuery } from '@uidotdev/usehooks'
import { Ban, CirclePlus, Loader2, MapPinned, MousePointer2, Target, X } from 'lucide-react'
import { useCallback, useEffect, useId, useMemo, useRef, useState } from 'react'
import { toast } from 'sonner'
import DeliveryAreaMap from '../DeliveryAreaMap/DeliveryAreaMap'
import {
  BASE_DELIVERY_AREA_COLOR,
  DEFAULT_DELIVERY_AREA_FILL_OPACITY,
  DEFAULT_SPECIALTY_LOCATION_FEE,
  MAX_SPECIALTY_LOCATIONS,
  MAX_EXCLUDED_AREAS,
  NON_DELIVERY_AREA_COLOR,
  SPECIALTY_LOCATION_COLOR,
  getSemanticPolygonFillOpacity,
} from '../DeliveryAreaMap/constants'
import { DeliveryAreaList } from './components/DeliveryAreaList'
import { FIELD_NAME, isSpecialPriceArea, placeRuleTypes } from './delivery-area-rule-utils'
import { useDeliveryAreaPlaceRules } from './useDeliveryAreaPlaceRules'
import {
  feeSourceLabels,
  formatMiles,
  formatTestAddress,
  useDeliveryMapTester,
} from './useDeliveryMapTester'

const sectionTitleIconClassName = 'size-4 shrink-0 text-muted-foreground'
const pointerTitleIconClassName = 'size-[18px] shrink-0 text-muted-foreground'

const MapModeButton = ({ active = false, children, className, ...props }) => (
  <ToggleButton
    type="button"
    pressed={active}
    size="sm"
    className={cn('h-9 w-full justify-start', className)}
    {...props}
  >
    {children}
  </ToggleButton>
)

const DeliveryAreaFees = ({ location = null }) => {
  const id = useId()
  const { isReadOnly } = useIsReadOnly()
  const form = useFormContext()
  const terraDrawRef = useRef(null)
  const initialDeliveryAreas = location?.delivery_area_fees || []
  const { maps, geometry } = useGoogleMaps(['maps', 'geometry'])
  const { fetchSuggestions, geocodeLatLng, getPlaceDetails } = usePlacesAutocomplete()
  const { mutateAsync: previewDeliveryFee } = usePreviewLocationDeliveryFeeMutation()
  const isWideMapLayout = useMediaQuery('(min-width: 1280px)')
  const fieldMeta = useStore(form.store, (state) => state.fieldMeta ?? {})

  // Use the form section hook
  const { data: allDeliveryAreas, setData: setDeliveryAreas } = useFormSection({
    fieldName: FIELD_NAME,
    initialData: initialDeliveryAreas,
    getDefaultData: () => [],
  })

  // UI State
  const [enableSpecialtyLocations, setEnableSpecialtyLocations] = useState(() => {
    return initialDeliveryAreas.some((area) => isSpecialPriceArea(area) && area.is_active)
  })
  const [enableExclusions, setEnableExclusions] = useState(() => {
    return initialDeliveryAreas.some(
      (area) => area.polygon_type === PolygonType.Excluded.Value && area.is_active
    )
  })
  const [selectedAreaIndex, setSelectedAreaIndex] = useState(null)
  const [drawingMode, setDrawingMode] = useState(null) // 'base' | 'location' | 'excluded' | null
  const [mapFocusRequestId, setMapFocusRequestId] = useState(0)
  const { baseArea, specialPriceAreas, excludedAreas } = useMemo(() => {
    const specialPriceAreaResults = []
    const excludedAreaResults = []
    let baseAreaResult = null

    allDeliveryAreas.forEach((area) => {
      if (area.polygon_type === PolygonType.Base.Value) {
        baseAreaResult ??= area
      }

      if (isSpecialPriceArea(area)) {
        specialPriceAreaResults.push(area)
      }

      if (area.polygon_type === PolygonType.Excluded.Value) {
        excludedAreaResults.push(area)
      }
    })

    return {
      baseArea: baseAreaResult,
      specialPriceAreas: specialPriceAreaResults,
      excludedAreas: excludedAreaResults,
    }
  }, [allDeliveryAreas])
  const hasBaseArea = !!baseArea && !!baseArea.encoded_polygon

  const handleSelectArea = (index, { shouldFocus = false } = {}) => {
    setSelectedAreaIndex(index)
    setDrawingMode(null)

    if (shouldFocus) {
      setMapFocusRequestId((requestId) => requestId + 1)
    }
  }

  const handlePlaceRuleAdded = (index) => handleSelectArea(index, { shouldFocus: true })

  const {
    clearPointTest,
    deliveryTest,
    isPointTestMode,
    startPointTest,
    testPoint: handlePointTest,
  } = useDeliveryMapTester({
    form,
    geocodeLatLng,
    isReadOnly,
    locationId: location?.id,
    previewDeliveryFee,
  })

  const { addPlaceRule, excludedPlaceSearch, isAddingPlaceRule, specialPlaceSearch } =
    useDeliveryAreaPlaceRules({
      allDeliveryAreas,
      excludedAreas,
      fetchSuggestions,
      form,
      geometry,
      getPlaceDetails,
      isReadOnly,
      maps,
      onExistingAreaSelected: (index) => handleSelectArea(index, { shouldFocus: true }),
      onPlaceRuleAdded: handlePlaceRuleAdded,
      setDeliveryAreas,
      setEnableExclusions,
      setEnableSpecialtyLocations,
      specialPriceAreas,
    })

  // Calculate drawing color based on current drawing mode
  const drawingColor =
    drawingMode === 'base'
      ? BASE_DELIVERY_AREA_COLOR
      : drawingMode === 'location'
        ? SPECIALTY_LOCATION_COLOR
        : drawingMode === 'excluded'
          ? NON_DELIVERY_AREA_COLOR
          : null
  const drawingFillOpacity = drawingMode
    ? getSemanticPolygonFillOpacity(drawingMode, DEFAULT_DELIVERY_AREA_FILL_OPACITY)
    : DEFAULT_DELIVERY_AREA_FILL_OPACITY

  // Calculate which polygons should be hidden on the map.
  const hiddenPolygonReferenceIds = useMemo(() => {
    const hidden = []

    specialPriceAreas.forEach((location) => {
      if (location.reference_id && (!enableSpecialtyLocations || !location.is_active)) {
        hidden.push(location.reference_id)
      }
    })
    excludedAreas.forEach((area) => {
      if (area.reference_id && (!enableExclusions || !area.is_active)) {
        hidden.push(area.reference_id)
      }
    })

    return hidden
  }, [enableExclusions, enableSpecialtyLocations, excludedAreas, specialPriceAreas])

  const handleCancelDrawing = useCallback(() => {
    setDrawingMode(null)

    try {
      if (terraDrawRef.current?.getMode?.() === 'polygon') {
        terraDrawRef.current.setMode('select')
      }
    } catch {
      // Terra Draw may not be initialized yet; the React state still exits draw mode.
    }
  }, [])

  // Handle drawing new areas
  const handleStartDrawing = (mode) => {
    if (mode === 'location' && specialPriceAreas.length >= MAX_SPECIALTY_LOCATIONS) {
      toast.error(`Maximum of ${MAX_SPECIALTY_LOCATIONS} special price places allowed`)
      return
    }
    if (mode === 'excluded' && excludedAreas.length >= MAX_EXCLUDED_AREAS) {
      toast.error(`Maximum of ${MAX_EXCLUDED_AREAS} no-delivery areas allowed`)
      return
    }

    clearPointTest()

    // Clear Terra Draw selection before starting drawing mode
    // This prevents the "double-click" issue where the first click exits edit mode
    if (terraDrawRef.current) {
      try {
        const currentMode = terraDrawRef.current.getMode()
        if (currentMode === 'select') {
          // Toggle mode to clear selection, then it will switch to polygon mode
          // via the externalDrawingMode effect in DeliveryAreaMap
          terraDrawRef.current.setMode('polygon')
          terraDrawRef.current.setMode('select')
        }
      } catch {
        // Ignore errors - Terra Draw might not be fully initialized
      }
    }

    setDrawingMode(mode)
    setSelectedAreaIndex(null)
  }

  const handleDeliveryAreaPolygonComplete = (geoJsonFeature, encodedPolygon) => {
    const currentAreas = allDeliveryAreas || []

    // Guard against duplicate reference_ids - this can happen if the handler fires twice
    const referenceId = geoJsonFeature.id
    if (currentAreas.some((area) => area.reference_id === referenceId)) {
      return // Polygon with this reference_id already exists
    }

    let newArea
    if (drawingMode === 'base' || (!hasBaseArea && !drawingMode)) {
      // Creating base area
      newArea = {
        encoded_polygon: encodedPolygon,
        name: 'Base Delivery Area',
        color: BASE_DELIVERY_AREA_COLOR,
        polygon_type: PolygonType.Base.Value,
        fee: null,
        delivery_cutoff: null,
        is_active: true,
        reference_id: geoJsonFeature.id,
      }
    } else if (drawingMode === 'location') {
      newArea = {
        encoded_polygon: encodedPolygon,
        name: `Special Price Place ${specialPriceAreas.length + 1}`,
        color: SPECIALTY_LOCATION_COLOR,
        polygon_type: PolygonType.Location.Value,
        fee: parseFloat(DEFAULT_SPECIALTY_LOCATION_FEE).toFixed(2),
        delivery_cutoff: null,
        is_active: true,
        reference_id: geoJsonFeature.id,
      }
    } else if (drawingMode === 'excluded') {
      newArea = {
        encoded_polygon: encodedPolygon,
        name: `No Delivery Area ${excludedAreas.length + 1}`,
        color: NON_DELIVERY_AREA_COLOR,
        polygon_type: PolygonType.Excluded.Value,
        fee: null,
        delivery_cutoff: null,
        is_active: true,
        reference_id: geoJsonFeature.id,
      }
    }

    if (newArea) {
      const newIndex = currentAreas.length
      setDeliveryAreas([...currentAreas, newArea])
      setSelectedAreaIndex(newIndex)
    }

    setDrawingMode(null)
  }

  // Handle area deletion
  const handleDeleteArea = (index) => {
    const currentAreas = allDeliveryAreas || []
    const areaToDelete = currentAreas[index]

    const updatedAreas = currentAreas.filter((_, i) => i !== index)
    setDeliveryAreas(updatedAreas, { touch: true })

    // Clear selection if deleted
    if (selectedAreaIndex === index) {
      setSelectedAreaIndex(null)
    } else if (selectedAreaIndex > index) {
      setSelectedAreaIndex(selectedAreaIndex - 1)
    }

    // Reset drawing mode if base area was deleted
    if (areaToDelete?.polygon_type === PolygonType.Base.Value && drawingMode === 'base') {
      setDrawingMode(null)
    }
  }

  const handleSpecialtyLocationsToggle = (enabled) => {
    setEnableSpecialtyLocations(enabled)
    const currentAreas = allDeliveryAreas || []
    const updatedAreas = currentAreas.map((area) => {
      if (isSpecialPriceArea(area)) {
        return { ...area, is_active: enabled }
      }

      return area
    })
    setDeliveryAreas(updatedAreas, { touch: true })
  }

  const handleExclusionsToggle = (enabled) => {
    setEnableExclusions(enabled)
    const currentAreas = allDeliveryAreas || []
    const updatedAreas = currentAreas.map((area) => {
      const isExcluded = area.polygon_type === PolygonType.Excluded.Value
      if (isExcluded) {
        return { ...area, is_active: enabled }
      }
      return area
    })
    setDeliveryAreas(updatedAreas, { touch: true })
  }

  const getGlobalIndex = (area) => allDeliveryAreas.indexOf(area)
  const getSelectedLocalIndex = (areas) =>
    areas.findIndex((area) => allDeliveryAreas.indexOf(area) === selectedAreaIndex)
  const getAreaValidationErrors = (areas) =>
    areas.reduce((errors, area, localIndex) => {
      const globalIndex = getGlobalIndex(area)
      if (globalIndex < 0) {
        return errors
      }

      const messages = getFieldErrorMessages(
        fieldMeta,
        `${FIELD_NAME}[${globalIndex}].encoded_polygon`
      )
      if (messages.length > 0) {
        errors[localIndex] = messages.join(' ')
      }

      return errors
    }, {})

  const handleTogglePointTestMode = () => {
    if (isReadOnly) {
      return
    }

    if (!location?.id) {
      toast.error('Save this location before testing delivery fees on the map.')
      return
    }

    handleCancelDrawing()
    setSelectedAreaIndex(null)

    if (isPointTestMode) {
      clearPointTest()
      return
    }

    startPointTest()
  }

  useEffect(() => {
    const handleKeyDown = (event) => {
      if (event.key !== 'Escape' || event.defaultPrevented) {
        return
      }

      if (isPointTestMode) {
        event.preventDefault()
        clearPointTest()
        return
      }

      if (drawingMode) {
        event.preventDefault()
        handleCancelDrawing()
      }
    }

    window.addEventListener('keydown', handleKeyDown)

    return () => {
      window.removeEventListener('keydown', handleKeyDown)
    }
  }, [clearPointTest, drawingMode, handleCancelDrawing, isPointTestMode])

  const deliveryTestResult = deliveryTest?.result ?? null
  const deliveryTestIsDeliverable =
    deliveryTestResult?.serviceability_status === DeliveryServiceabilityStatus.Deliverable.Value
  const deliveryTestStatusLabel = deliveryTestIsDeliverable ? 'Delivery available' : 'No delivery'
  const deliveryTestFeeLabel =
    deliveryTestResult?.fee_amount !== null && deliveryTestResult?.fee_amount !== undefined
      ? formatCurrency(deliveryTestResult.fee_amount)
      : 'No fee'
  const deliveryTestSourceLabel =
    feeSourceLabels[deliveryTestResult?.fee_source] ?? deliveryTestResult?.fee_source ?? 'No match'
  const deliveryTestCutoff = deliveryTestResult?.effective_delivery_cutoff
  const deliveryTestEarliestDate = deliveryTestResult?.earliest_delivery_date

  const deliveryMapControls = (
    <div className="relative z-10 space-y-4">
      <div
        className={cn(
          'rounded-md border p-3',
          isPointTestMode && 'border-status-info-border bg-status-info-background'
        )}
      >
        <div className="mb-3 flex items-start justify-between gap-3">
          <div className="flex items-start gap-2">
            <MousePointer2 className={cn('mt-0.5', pointerTitleIconClassName)} />
            <div>
              <Typography variant="cardTitle">Test a location</Typography>
              <Typography variant="caption" className="block">
                Click the button, then click different spots on the map to see if delivery is
                available and what you would charge with your current settings.
              </Typography>
            </div>
          </div>
        </div>

        <div className="space-y-3">
          <MapModeButton
            active={isPointTestMode}
            onClick={handleTogglePointTestMode}
            disabled={isReadOnly || !location?.id}
          >
            {isPointTestMode ? (
              <>
                <X className="h-4 w-4" />
                Stop testing
              </>
            ) : (
              <>
                <MousePointer2 className="h-4 w-4" />
                Test a location
              </>
            )}
          </MapModeButton>

          {!location?.id ? (
            <Typography variant="caption" className="block">
              Save this location before testing map fees.
            </Typography>
          ) : null}

          {isPointTestMode ? (
            <Typography variant="caption" className="block">
              Click a point on the map to run the test.
            </Typography>
          ) : null}

          {deliveryTest?.status === 'loading' ? (
            <div className="flex items-center gap-2 rounded-md border bg-muted/30 px-3 py-2 text-sm">
              <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
              Checking route distance and delivery fee...
            </div>
          ) : null}

          {deliveryTest?.status === 'error' ? (
            <div className="rounded-md border border-status-destructive-border bg-status-destructive-background px-3 py-2 text-sm text-status-destructive-foreground">
              {deliveryTest.error}
            </div>
          ) : null}

          {deliveryTest?.status === 'success' ? (
            <div
              className={cn(
                'space-y-2 rounded-md border px-3 py-2 text-sm',
                deliveryTestIsDeliverable
                  ? 'border-status-success-border bg-status-success-background'
                  : 'border-status-destructive-border bg-status-destructive-background'
              )}
            >
              <div className="flex items-center justify-between gap-3">
                <span className="font-medium">{deliveryTestStatusLabel}</span>
                <span className="font-semibold">{deliveryTestFeeLabel}</span>
              </div>
              <div className="grid gap-1 text-xs text-muted-foreground">
                <span>Route distance: {formatMiles(deliveryTestResult?.route_distance_miles)}</span>
                <span>Matched rule: {deliveryTestSourceLabel}</span>
                <span>
                  Effective delivery cutoff:{' '}
                  {deliveryTestCutoff ? `${deliveryTestCutoff} business hours` : 'Unavailable'}
                </span>
                <span>
                  Earliest delivery:{' '}
                  {deliveryTestEarliestDate
                    ? dayjs(deliveryTestEarliestDate, 'YYYY-MM-DD').format('ddd, MMM D')
                    : 'Unavailable'}
                </span>
                <span>{formatTestAddress(deliveryTest.address, deliveryTest.point)}</span>
              </div>
            </div>
          ) : null}
        </div>
      </div>

      <div className="rounded-md border p-3">
        <div className="mb-2 flex items-center justify-between gap-3">
          <div className="flex items-center gap-2">
            <MapPinned className={sectionTitleIconClassName} />
            <Typography variant="cardTitle">Delivery boundary</Typography>
          </div>
        </div>

        {!hasBaseArea ? (
          <div className="space-y-3">
            <Typography variant="caption" className="block">
              Draw a custom boundary on the map to limit delivery orders to addresses within this
              area.
            </Typography>
            {!isReadOnly &&
              (drawingMode === 'base' ? (
                <MapModeButton active onClick={handleCancelDrawing}>
                  <X className="h-4 w-4" />
                  Cancel boundary
                </MapModeButton>
              ) : (
                <MapModeButton
                  active={false}
                  onClick={() => handleStartDrawing('base')}
                  disabled={drawingMode !== null}
                >
                  <CirclePlus className="h-4 w-4" />
                  Draw boundary
                </MapModeButton>
              ))}
          </div>
        ) : (
          <div className="space-y-2">
            <DeliveryAreaList
              areas={[baseArea]}
              areaType="base"
              selectedIndex={getGlobalIndex(baseArea) === selectedAreaIndex ? 0 : null}
              onSelect={() => handleSelectArea(getGlobalIndex(baseArea), { shouldFocus: true })}
              onDelete={() => handleDeleteArea(getGlobalIndex(baseArea))}
              getGlobalIndex={getGlobalIndex}
              validationErrors={getAreaValidationErrors([baseArea])}
              canDelete
            />
            <Typography variant="caption" className="block">
              Verified addresses outside this boundary cannot check out unless they match a special
              price place. An optional fallback fee covers addresses inside the boundary that do not
              match a ZIP code, city, or driving-distance fee.
            </Typography>
          </div>
        )}
      </div>

      <div className="rounded-md border p-3">
        <div className="mb-3 flex items-center justify-between gap-3">
          <div className="flex items-center gap-2">
            <Target className={sectionTitleIconClassName} />
            <Label
              htmlFor={`${id}-locations-toggle`}
              appearance="choice"
              size="body"
              weight="semibold"
            >
              Special price places
            </Label>
          </div>
          <Switch
            id={`${id}-locations-toggle`}
            checked={enableSpecialtyLocations}
            onCheckedChange={handleSpecialtyLocationsToggle}
            disabled={isReadOnly}
          />
        </div>

        <div className="space-y-3">
          <div className="space-y-2">
            <AutocompleteSearchInput
              inputRef={specialPlaceSearch.inputRef}
              value={specialPlaceSearch.search}
              setValue={specialPlaceSearch.setSearch}
              suggestions={specialPlaceSearch.suggestions}
              onSelect={(suggestion) => addPlaceRule(suggestion, placeRuleTypes.location)}
              placeholder="Search for a special price place"
              disabled={isReadOnly || isAddingPlaceRule}
              onClear={specialPlaceSearch.clear}
              getOptionKey={(suggestion) => suggestion.id}
              getOptionLabel={(suggestion) => suggestion.name}
            />
            <Typography variant="caption" className="block">
              Override boundaries, distance limits, and no-delivery areas for special locations,
              like funeral homes and hospitals. When custom delivery cutoffs are on, each place can
              also use its own cutoff.
            </Typography>
          </div>

          {specialPriceAreas.length > 0 ? (
            <DeliveryAreaList
              areas={specialPriceAreas}
              areaType="location"
              selectedIndex={getSelectedLocalIndex(specialPriceAreas)}
              onSelect={(localIndex) =>
                handleSelectArea(getGlobalIndex(specialPriceAreas[localIndex]), {
                  shouldFocus: true,
                })
              }
              onDelete={(localIndex) =>
                handleDeleteArea(getGlobalIndex(specialPriceAreas[localIndex]))
              }
              getGlobalIndex={getGlobalIndex}
              validationErrors={getAreaValidationErrors(specialPriceAreas)}
              canDelete
            />
          ) : (
            <Typography variant="caption" className="block">
              No special price places yet.
            </Typography>
          )}

          {!isReadOnly &&
            enableSpecialtyLocations &&
            (drawingMode === 'location' ? (
              <MapModeButton active onClick={handleCancelDrawing}>
                <X className="h-4 w-4" />
                Cancel special area
              </MapModeButton>
            ) : (
              <MapModeButton
                active={false}
                onClick={() => handleStartDrawing('location')}
                disabled={
                  drawingMode !== null || specialPriceAreas.length >= MAX_SPECIALTY_LOCATIONS
                }
              >
                <CirclePlus className="h-4 w-4" />
                Draw special area
              </MapModeButton>
            ))}
        </div>
      </div>

      <div className="rounded-md border p-3">
        <div className="mb-3 flex items-center justify-between gap-3">
          <div className="flex items-center gap-2">
            <Ban className={sectionTitleIconClassName} />
            <Label
              htmlFor={`${id}-exclusions-toggle`}
              appearance="choice"
              size="body"
              weight="semibold"
            >
              No-delivery places
            </Label>
          </div>
          <Switch
            id={`${id}-exclusions-toggle`}
            checked={enableExclusions}
            onCheckedChange={handleExclusionsToggle}
            disabled={isReadOnly}
          />
        </div>

        <div className="space-y-3">
          <div className="space-y-2">
            <AutocompleteSearchInput
              inputRef={excludedPlaceSearch.inputRef}
              value={excludedPlaceSearch.search}
              setValue={excludedPlaceSearch.setSearch}
              suggestions={excludedPlaceSearch.suggestions}
              onSelect={(suggestion) => addPlaceRule(suggestion, placeRuleTypes.excluded)}
              placeholder="Search for a no-delivery place"
              disabled={isReadOnly || isAddingPlaceRule}
              onClear={excludedPlaceSearch.clear}
              getOptionKey={(suggestion) => suggestion.id}
              getOptionLabel={(suggestion) => suggestion.name}
            />
            <Typography variant="caption" className="block">
              Search an airport, campus, or restricted area, then adjust the area if needed. Special
              price places still take priority.
            </Typography>
          </div>

          {excludedAreas.length > 0 ? (
            <DeliveryAreaList
              areas={excludedAreas}
              areaType="excluded"
              selectedIndex={getSelectedLocalIndex(excludedAreas)}
              onSelect={(localIndex) =>
                handleSelectArea(getGlobalIndex(excludedAreas[localIndex]), {
                  shouldFocus: true,
                })
              }
              onDelete={(localIndex) => handleDeleteArea(getGlobalIndex(excludedAreas[localIndex]))}
              getGlobalIndex={getGlobalIndex}
              validationErrors={getAreaValidationErrors(excludedAreas)}
              canDelete
            />
          ) : (
            <Typography variant="caption" className="block">
              No no-delivery rules yet.
            </Typography>
          )}

          {!isReadOnly &&
            enableExclusions &&
            (drawingMode === 'excluded' ? (
              <MapModeButton active onClick={handleCancelDrawing}>
                <X className="h-4 w-4" />
                Cancel no-delivery area
              </MapModeButton>
            ) : (
              <MapModeButton
                active={false}
                onClick={() => handleStartDrawing('excluded')}
                disabled={drawingMode !== null || excludedAreas.length >= MAX_EXCLUDED_AREAS}
              >
                <CirclePlus className="h-4 w-4" />
                Draw no-delivery area
              </MapModeButton>
            ))}
        </div>
      </div>
    </div>
  )

  const deliveryMapCanvas = (
    <div className="min-w-0 @6xl:sticky @6xl:top-4">
      <DeliveryAreaMap
        location={location}
        showPolygonList="others-only"
        onPolygonComplete={handleDeliveryAreaPolygonComplete}
        selectedPolygonIndex={selectedAreaIndex}
        focusRequestId={mapFocusRequestId}
        onPolygonSelect={(index) => handleSelectArea(index)}
        drawingMode={drawingMode}
        drawingColor={drawingColor}
        drawingFillOpacity={drawingFillOpacity}
        onStopDrawing={() => setDrawingMode(null)}
        terraDrawRef={terraDrawRef}
        hiddenPolygonReferenceIds={hiddenPolygonReferenceIds}
        isPointTestMode={isPointTestMode}
        onPointTest={handlePointTest}
        pointTestMarker={deliveryTest?.point ?? null}
        mapHeightClassName="h-[min(76vh,54rem)] min-h-[38rem]"
      />
    </div>
  )

  return (
    <section className="@container space-y-4">
      <div>
        <Typography variant="cardTitle">Delivery map</Typography>
        <Typography variant="supporting">
          Test any point on the map to preview availability and fees, then draw optional delivery
          boundaries, special-price places, and no-delivery areas as needed.
        </Typography>
      </div>

      {isWideMapLayout ? (
        <ResizablePanelGroup direction="horizontal" className="min-h-[38rem] items-stretch">
          <ResizablePanel defaultSize={30} minSize={24} maxSize={46} className="min-w-[24rem]">
            <div className="h-full min-h-0 pr-2">{deliveryMapControls}</div>
          </ResizablePanel>

          <ResizableHandle
            withHandle
            className="mx-1 w-2 rounded-full bg-transparent transition-colors hover:bg-primary/15"
          />

          <ResizablePanel defaultSize={70} minSize={50}>
            <div className="h-full min-h-0 pl-2">{deliveryMapCanvas}</div>
          </ResizablePanel>
        </ResizablePanelGroup>
      ) : (
        <div className="grid gap-4">
          {deliveryMapControls}
          {deliveryMapCanvas}
        </div>
      )}
    </section>
  )
}

export default DeliveryAreaFees
